import { CommonRfiUtils, Constants, MarkupDoc, RfiDoc, RfiFeedItemDoc, RFISettingsDoc, WorkflowState } from 'common';
import { ConcourseDb, DbListener } from '../../global/ConcourseDb';
import { authFetch, getAccessToken } from '../../global/service/AuthService';

export class RfiService {
  private static getBaseUrl(): string {
    return `${globalThis.ccLambdaBaseUrl}/fn/rfi`;
  }

  static getFeedDb(projectId: string, rfiId: string): ConcourseDb {
    return ConcourseDb.getInstance(CommonRfiUtils.getFeedDbName(projectId, rfiId));
  }

  static getMarkupsDb(projectId: string, rfiId: string): ConcourseDb {
    return ConcourseDb.getInstance(CommonRfiUtils.getMarkupsDbName(projectId, rfiId));
  }

  static async storeFeedItem(projectId: string, rfiId: string, item: RfiFeedItemDoc): Promise<RfiFeedItemDoc> {
    const db = this.getFeedDb(projectId, rfiId);
    return await db.saveDoc(item);
  }

  static async storeMarkup(projectId: string, rfiId: string, markup: MarkupDoc): Promise<MarkupDoc> {
    const db = this.getMarkupsDb(projectId, rfiId);
    return await db.saveDoc(markup);
  }

  /** Generate a fresh RFI PDF and download it. */
  static async exportPdf(projectId: string, rfiId: string): Promise<void> {
    const response = await authFetch(`${this.getBaseUrl()}/export-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, rfiId }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to export RFI PDF: ${errorData.message || response.statusText}`);
    }
    const data = await response.json();
    if (!data?.signedUrl) {
      throw new Error(data?.message || 'Failed to export RFI PDF');
    }
    await this.downloadFromSignedUrl(data.signedUrl, data.fileName || `rfi-${rfiId}.pdf`);
  }

  static async downloadFromSignedUrl(signedUrl: string, fileName: string): Promise<void> {
    const response = await fetch(signedUrl);
    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }

  static getRfisDbName(projectId: string): string {
    return `p-${projectId}-rfis`;
  }

  static getRfisDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(this.getRfisDbName(projectId));
  }

  static filterRfiDocs(docs: any[]): RfiDoc[] {
    return (docs || []).filter(doc => doc && !Constants.isSettingsDoc(doc) && !String(doc._id || '').startsWith('_'));
  }

  static findSettingsDoc(docs: any[]): RFISettingsDoc | undefined {
    return (docs || []).find(doc => Constants.isSettingsDoc(doc)) as RFISettingsDoc | undefined;
  }

  static async getRfiSettings(projectId: string): Promise<RFISettingsDoc> {
    const db = this.getRfisDb(projectId);
    const settingsDoc = await db.getDoc(Constants.SETTINGS_DOC_ID);
    return settingsDoc;
  }

  static async loadRfiSettings(projectId: string, listener: DbListener): Promise<void> {
    const db = this.getRfisDb(projectId);
    db.addListeners(listener);
    await db.syncRemote();
  }

  static async saveSettings(projectId: string, settings: RFISettingsDoc): Promise<RFISettingsDoc> {
    const db = this.getRfisDb(projectId);
    settings._id = Constants.SETTINGS_DOC_ID;
    return await db.saveDoc(settings);
  }

  /**
   * To save a draft we put it in the list db with all its data.
   */
  static async saveDraft(projectId: string, detail: RfiDoc): Promise<RfiDoc> {
    const db = this.getRfisDb(projectId);
    const doc = await db.saveDoc({ ...detail });
    return doc as RfiDoc;
  }

  /** Any attachments added while in draft status need to be removed from the list db */
  static async removeAttachment(projectId: string, rfi: RfiDoc, filename: string): Promise<RfiDoc> {
    const db = this.getRfisDb(projectId);
    const result = await db.removeAttachment(rfi._id, filename, rfi._rev);
    if (result && result.ok) {
      return new Promise(resolve => {
        resolve({ ...rfi, _rev: result.rev });
      });
    } else {
      throw new Error('Failed to remove attachment');
    }
  }

  static async saveRfiList(projectId: string, list: RfiDoc[]): Promise<RfiDoc[]> {
    const db = this.getRfisDb(projectId);
    db.bulkDocs(list);
    return await db.getDocs();
  }

  static async saveRfi(projectId: string, rfi: RfiDoc): Promise<RfiDoc> {
    const db = this.getRfisDb(projectId);
    return await db.update(rfi);
  }

  /**
   * We get the RFI id from the last RFI in the list and increment it by 1.
   * We know this is only based on locally cached ids so the create method will throw errors.
   */
  static async getNextRfiNumber(projectId: string): Promise<string> {
    const db = this.getRfisDb(projectId);
    const docs: RfiDoc[] = await db.getDocs();
    const docsNumbers: number[] = docs.filter(doc => !isNaN(Number(doc.code))).map(doc => Number(doc.code));
    return (docsNumbers.length > 0 ? Math.max(...docsNumbers) + 1 : 1).toString();
  }

  static async createRfi(projectId: string, rfi: RfiDoc): Promise<RfiDoc> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/rfi/create?projectId=' + projectId;
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ rfi }),
    });

    if (response.status !== 200) {
      throw new Error(await response.text());
    }

    const json = await response.json();
    return json.rfi;
  }

  /**
   * Complete the next workflow state server-side (forward only).
   * Server loads the RFI from CouchDB; optional nextState merges dueBy onto the matching state.
   *
   * Maps to: POST /fn/:projectId/rfi/:rfiId/complete-state
   */
  static async completeState(
    projectId: string,
    rfiId: string,
    stateId: string,
    nextState?: WorkflowState,
    acceptedAnswer?: string,
  ): Promise<RfiDoc> {
    const url = `${globalThis.ccLambdaBaseUrl}/fn/${projectId}/rfi/${rfiId}/complete-state`;
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ stateId, nextState, acceptedAnswer }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to complete RFI state: ${errorData.message || response.statusText}`);
    }

    const json = await response.json();
    return json.rfi as RfiDoc;
  }

  /**
   * Step back one completed workflow state. Server uses completed_state_index on the DB doc.
   *
   * Maps to: POST /fn/:projectId/rfi/:rfiId/return-to-state
   */
  static async returnToState(projectId: string, rfiId: string, message: string): Promise<RfiDoc> {
    const url = `${globalThis.ccLambdaBaseUrl}/fn/${projectId}/rfi/${rfiId}/return-to-state`;
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ message }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to return RFI state: ${errorData.message || response.statusText}`);
    }

    const json = await response.json();
    return json.rfi as RfiDoc;
  }

  static async notifyStatusComplete(projectId: string, stateId: string, rfi: RfiDoc): Promise<Response> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/rfi/notify-status-complete';
    return await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ projectId, rfi, stateId }),
    });
  }
}
